/**
 * TriaxialAutoPullPanel — Auto-pulled-from-same-Sample panel for the
 * Triaxial Parameters tab.
 *
 * REQ-2026-0002 (REQ-08) hardening:
 *
 *   1) Always renders, even when no upstream Approved test exists on the
 *      sample. Missing values show "—" with an inline ⓘ tooltip.
 *
 *   2) Each overrideable field (USCS, Fines, Silt, Clay, LL, PL, PI) is
 *      read-only by default. A small "Override" button opens an inline
 *      editor. When overridden, the auto-pulled value is rendered in a
 *      strikethrough caption underneath the editable input. A "Reset"
 *      action clears the override (sets it to null) and restores the
 *      auto-pulled value as the displayed value.
 *
 *   3) Gs is auto-filled into the main Specific Gravity Gs field by the
 *      Parameters tab via a one-shot effect (see
 *      `useEffect` on `crossTest?.specificGravityGs` in
 *      TriaxialParametersTab.tsx); the Gs cell here is purely
 *      informational, not overrideable from this panel.
 *
 *   4) The cross-test data passed in is the upstream Approved test only —
 *      the backend SQL filters to wod.TestStatus = 'Approved'. The tooltip
 *      strings reflect that contract.
 */
import { useState } from 'react';
import {
  Box,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { TriaxialCrossTest, TriaxialGeneral } from '../../../types/triaxial';

interface Props {
  general: TriaxialGeneral;
  crossTest: TriaxialCrossTest | null;
  onChange: (field: keyof TriaxialGeneral, value: string | number | null) => void;
  readOnly?: boolean;
}

const TOOLTIPS = {
  noAtterberg: 'No approved Atterberg test for this sample.',
  noPsd: 'No approved PSD test for this sample.',
  approvedOnly: 'Only Approved upstream tests are pulled in.',
} as const;

/** Format a number for display; '—' when null/undefined. */
const fmt = (v: number | null | undefined, digits = 0): string => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(digits) : '—';
};

/** One overrideable cell: header + (read-only auto-pulled OR editable override). */
function OverrideCell<F extends keyof TriaxialGeneral>(props: {
  label: string;
  /** Effective value (override ?? auto). */
  effective: string;
  /** Raw auto-pulled value, for the strikethrough display when overridden. */
  autoPulled: string | null;
  /** Whether the operator has an override stored. */
  hasOverride: boolean;
  /** Whether the upstream Approved test exists for this field's data. */
  upstreamApproved: boolean;
  /** Tooltip shown on the ⓘ icon when upstream is missing. */
  missingTooltip: string;
  /** Override column on TriaxialGeneral. */
  field: F;
  /** Current override value (may be null). */
  overrideValue: TriaxialGeneral[F];
  /** Edit-mode toggle (visible across cells in the same panel). */
  editing: boolean;
  setEditing: (v: boolean) => void;
  /** Parent onChange. */
  onChange: (field: keyof TriaxialGeneral, value: string | number | null) => void;
  /** Number / string field — affects keyboard + parsing. */
  kind: 'number' | 'string';
  readOnly?: boolean;
}) {
  const {
    label, effective, autoPulled, hasOverride, upstreamApproved, missingTooltip,
    field, overrideValue, editing, setEditing, onChange, kind, readOnly,
  } = props;

  const handleStart = () => setEditing(true);
  const handleCancel = () => setEditing(false);
  const handleReset = () => {
    onChange(field, null);
    setEditing(false);
  };
  const handleCommit = (raw: string) => {
    if (raw === '') {
      onChange(field, null);
      return;
    }
    if (kind === 'number') {
      const n = Number(raw);
      onChange(field, Number.isFinite(n) ? n : null);
    } else {
      onChange(field, raw);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          {label}
        </Typography>
        {!upstreamApproved && !hasOverride && (
          <Tooltip title={missingTooltip} placement="top">
            <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled', cursor: 'help' }} />
          </Tooltip>
        )}
      </Box>

      {editing && !readOnly ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <TextField
            size="small"
            autoFocus
            type={kind === 'number' ? 'number' : 'text'}
            defaultValue={overrideValue == null ? '' : String(overrideValue)}
            onBlur={(e) => { handleCommit(e.target.value); setEditing(false); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { handleCommit((e.target as HTMLInputElement).value); setEditing(false); }
              else if (e.key === 'Escape') handleCancel();
            }}
            sx={{
              '& .MuiInputBase-input': { py: 0.25, px: 0.75, fontSize: '0.78rem', fontWeight: 600 },
              maxWidth: 90,
            }}
            inputProps={{ 'aria-label': `Override ${label}` }}
          />
          {hasOverride && (
            <Tooltip title="Reset override (restore auto-pulled)">
              <IconButton size="small" onClick={handleReset} sx={{ p: 0.25 }} aria-label={`Reset ${label} override`}>
                <RestartAltIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: hasOverride ? 'warning.dark' : 'text.primary',
            }}
          >
            {effective}
          </Typography>
          {!readOnly && (
            <Tooltip title={hasOverride ? 'Edit override' : 'Override auto-pulled value'}>
              <IconButton size="small" onClick={handleStart} sx={{ p: 0.25 }} aria-label={`Override ${label}`}>
                <EditIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* Strikethrough auto-pulled value when override is active */}
      {hasOverride && autoPulled !== null && (
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.62rem',
            color: 'text.disabled',
            textDecoration: 'line-through',
            lineHeight: 1.2,
          }}
        >
          auto-pulled: {autoPulled}
        </Typography>
      )}
    </Box>
  );
}

export function TriaxialAutoPullPanel({ general, crossTest, onChange, readOnly }: Props) {
  // Edit-mode flags (one per field). Stored locally so opening one editor
  // doesn't open all of them.
  const [editing, setEditing] = useState<Partial<Record<keyof TriaxialGeneral, boolean>>>({});
  const setField = (f: keyof TriaxialGeneral, v: boolean) =>
    setEditing((prev) => ({ ...prev, [f]: v }));

  // Effective values (override ?? auto ?? null)
  const eff = {
    uscs: (general.overrideUSCSDescription ?? crossTest?.uscsDescription ?? null) as string | null,
    fines: (general.overrideFinesPct ?? crossTest?.finesPct ?? null) as number | null,
    silt:  (general.overrideSiltPct  ?? crossTest?.siltPct  ?? null) as number | null,
    clay:  (general.overrideClayPct  ?? crossTest?.clayPct  ?? null) as number | null,
    ll:    (general.overrideLL       ?? crossTest?.liquidLimit    ?? null) as number | null,
    pl:    (general.overridePL       ?? crossTest?.plasticLimit   ?? null) as number | null,
    pi:    (general.overridePI       ?? crossTest?.plasticityIndex ?? null) as number | null,
  };

  // Per-field "auto-pulled" string (for strikethrough display when override
  // is active). Null when there's no upstream value at all.
  const auto = {
    uscs: crossTest?.uscsDescription ?? null,
    fines: crossTest?.finesPct != null ? fmt(crossTest.finesPct, 0) : null,
    silt:  crossTest?.siltPct  != null ? fmt(crossTest.siltPct, 0) : null,
    clay:  crossTest?.clayPct  != null ? fmt(crossTest.clayPct, 0) : null,
    ll:    crossTest?.liquidLimit    != null ? fmt(crossTest.liquidLimit, 0) : null,
    pl:    crossTest?.plasticLimit   != null ? fmt(crossTest.plasticLimit, 0) : null,
    pi:    crossTest?.plasticityIndex != null ? fmt(crossTest.plasticityIndex, 0) : null,
  };

  // Upstream-presence flags drive the tooltip on the empty cells.
  const psdPresent = crossTest?.uscsDescription != null
    || crossTest?.finesPct  != null
    || crossTest?.siltPct   != null
    || crossTest?.clayPct   != null
    || crossTest?.specificGravityGs != null;
  const attPresent = crossTest?.liquidLimit    != null
    || crossTest?.plasticLimit   != null
    || crossTest?.plasticityIndex != null;

  // Override presence per field (drives strikethrough + amber styling).
  const has = {
    uscs: general.overrideUSCSDescription != null && general.overrideUSCSDescription !== '',
    fines: general.overrideFinesPct != null,
    silt:  general.overrideSiltPct  != null,
    clay:  general.overrideClayPct  != null,
    ll:    general.overrideLL       != null,
    pl:    general.overridePL       != null,
    pi:    general.overridePI       != null,
  };

  return (
    <Paper sx={{ p: 1.5, bgcolor: 'info.lighter', borderLeft: '3px solid', borderColor: 'info.main' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="overline" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.7rem', letterSpacing: 0.6 }}>
          Auto-pulled from same Sample
        </Typography>
        <Tooltip title={TOOLTIPS.approvedOnly} placement="top">
          <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary', cursor: 'help' }} />
        </Tooltip>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 1.5, alignItems: 'start' }}>
        <OverrideCell
          label="USCS"
          effective={eff.uscs ?? '—'}
          autoPulled={auto.uscs}
          hasOverride={has.uscs}
          upstreamApproved={psdPresent}
          missingTooltip={TOOLTIPS.noPsd}
          field="overrideUSCSDescription"
          overrideValue={general.overrideUSCSDescription}
          editing={!!editing.overrideUSCSDescription}
          setEditing={(v) => setField('overrideUSCSDescription', v)}
          onChange={onChange}
          kind="string"
          readOnly={readOnly}
        />
        <OverrideCell
          label="Fines (%)"
          effective={fmt(eff.fines, 0)}
          autoPulled={auto.fines}
          hasOverride={has.fines}
          upstreamApproved={psdPresent}
          missingTooltip={TOOLTIPS.noPsd}
          field="overrideFinesPct"
          overrideValue={general.overrideFinesPct}
          editing={!!editing.overrideFinesPct}
          setEditing={(v) => setField('overrideFinesPct', v)}
          onChange={onChange}
          kind="number"
          readOnly={readOnly}
        />
        <OverrideCell
          label="Silt (%)"
          effective={fmt(eff.silt, 0)}
          autoPulled={auto.silt}
          hasOverride={has.silt}
          upstreamApproved={psdPresent}
          missingTooltip={TOOLTIPS.noPsd}
          field="overrideSiltPct"
          overrideValue={general.overrideSiltPct}
          editing={!!editing.overrideSiltPct}
          setEditing={(v) => setField('overrideSiltPct', v)}
          onChange={onChange}
          kind="number"
          readOnly={readOnly}
        />
        <OverrideCell
          label="Clay (%)"
          effective={fmt(eff.clay, 0)}
          autoPulled={auto.clay}
          hasOverride={has.clay}
          upstreamApproved={psdPresent}
          missingTooltip={TOOLTIPS.noPsd}
          field="overrideClayPct"
          overrideValue={general.overrideClayPct}
          editing={!!editing.overrideClayPct}
          setEditing={(v) => setField('overrideClayPct', v)}
          onChange={onChange}
          kind="number"
          readOnly={readOnly}
        />
        <OverrideCell
          label="LL (%)"
          effective={fmt(eff.ll, 0)}
          autoPulled={auto.ll}
          hasOverride={has.ll}
          upstreamApproved={attPresent}
          missingTooltip={TOOLTIPS.noAtterberg}
          field="overrideLL"
          overrideValue={general.overrideLL}
          editing={!!editing.overrideLL}
          setEditing={(v) => setField('overrideLL', v)}
          onChange={onChange}
          kind="number"
          readOnly={readOnly}
        />
        <OverrideCell
          label="PL (%)"
          effective={fmt(eff.pl, 0)}
          autoPulled={auto.pl}
          hasOverride={has.pl}
          upstreamApproved={attPresent}
          missingTooltip={TOOLTIPS.noAtterberg}
          field="overridePL"
          overrideValue={general.overridePL}
          editing={!!editing.overridePL}
          setEditing={(v) => setField('overridePL', v)}
          onChange={onChange}
          kind="number"
          readOnly={readOnly}
        />
        <OverrideCell
          label="PI (%)"
          effective={fmt(eff.pi, 0)}
          autoPulled={auto.pi}
          hasOverride={has.pi}
          upstreamApproved={attPresent}
          missingTooltip={TOOLTIPS.noAtterberg}
          field="overridePI"
          overrideValue={general.overridePI}
          editing={!!editing.overridePI}
          setEditing={(v) => setField('overridePI', v)}
          onChange={onChange}
          kind="number"
          readOnly={readOnly}
        />

        {/* Gs is informational only — it auto-fills SpecificGravityGs via a
            one-shot effect in TriaxialParametersTab; users edit Gs directly
            in the main parameters block, not here. */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Gs (from PSD)
            </Typography>
            {crossTest?.specificGravityGs == null && (
              <Tooltip title={TOOLTIPS.noPsd} placement="top">
                <InfoOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled', cursor: 'help' }} />
              </Tooltip>
            )}
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {crossTest?.specificGravityGs != null ? fmt(crossTest.specificGravityGs, 2) : '—'}
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}
